import React, { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import styled from 'styled-components/macro';
import { ContentBlock, Form, FormRow } from '../../forms/layoutComponents';
import { FormPageContainer, H3 } from '../../forms';
import { InputField, TextAreaElement } from '../../../components/common/forms';
import { Body2, Button, LinkButton } from '../../../components/common';
import { useMessageGetter } from 'react-message-context';
import { TerminationReasonDropdown } from './TerminationReasonDropdown';
import { Dropdown, DropdownOptionValue } from '../../../components/common/Dropdown';
import { newTerminationNoticeSchema, NewTerminationNotice } from './schema';
import { formatAddress, getContractEndDates, isFinnishIBAN, isForeignIBAN } from '../../../utils';
import { useKennoTenant } from '../../../context/kennoTenantContext';
import { loadContract } from '../../../context/kennoTenantActions';
import { ButtonContainer } from '../../layoutComponents';
import { YSaatioRoutes } from '../../../constants/routes';
import { initializeTerminationNotice } from '../../../apis_kenno';
import { H1 } from '../../../components/basic/Text';
import { FlexColumn } from '../../../components/basic/Flex';
import { RadioGroup } from '../../../components/common/RadioGroup';
import { Divider } from '../../../components/common/Divider';
import { getAdjustedTerminationFormData } from './terminationUtils';

const FORM_KEY = 'terminationNoticeForm';

const Description = styled(Body2)`
  margin: 20px 0;
`;
const TerminationDetailsContent = styled(ContentBlock)``;
const DisplayContent = styled(ContentBlock)``;
const AddressContent = styled(ContentBlock)``;
const BeneficiariesContent = styled(ContentBlock)``;

type Props = {
  afterSubmit: (url: string) => void;
};

export const TerminationNoticeForm: React.FC<Props> = ({ afterSubmit }) => {
  const translationPrefix = 'forms.terminateLease';
  const t = useMessageGetter(translationPrefix);
  const tCommon = useMessageGetter('common');
  const [isBusy, setIsBusy] = useState(false);
  const [{ contract, user }, dispatch] = useKennoTenant();

  useEffect(() => {
    loadContract(dispatch);
  }, [dispatch]);

  const contractEndDates = getContractEndDates();
  const firstPossibleEndDate = contractEndDates[0].value;

  const formMethods = useForm<NewTerminationNotice>({
    resolver: zodResolver(newTerminationNoticeSchema),
    defaultValues: {
      endDate: firstPossibleEndDate,
      movingOutDate: null,
      additionalDetails: '',
      newAddress: {
        recipientName: `${user.attributes?.firstName} ${user.attributes?.lastName}`,
      },
      beneficiaries: [
        {
          iban: '',
          bic: null,
          email: user.attributes?.email || '',
          phoneNumber: user.attributes?.phoneNumber || '',
          beneficiaryName: `${user.attributes?.firstName} ${user.attributes?.lastName}`,
          returnPercentage: 100,
          existingBeneficiaryId: null,
          removeExistingBeneficiary: true,
          contractToTenantAssociationId: null,
        },
      ],
      givingContactInfoAllowed: false,
      masterKeyAccessAllowed: false,
      hasPetsInApartment: false,
      displaysSelf: false,
      isCollateral: null,
      collateralLender: null,
      fileUuids: [],
    },
  });
  const { watch, setValue, control } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'beneficiaries',
  });

  const handleAddNewBeneficiary = () => {
    append({
      iban: '',
      bic: null,
      email: '',
      phoneNumber: '',
      beneficiaryName: '',
      returnPercentage: 100,
      existingBeneficiaryId: null,
      removeExistingBeneficiary: false,
      contractToTenantAssociationId: null,
    });
  };

  const beneficiaries = useWatch({ name: 'beneficiaries', control });
  // Ensure 'bic' is reset to null for Finnish bank accounts, but prohibit null
  // values for foreign bank accounts
  useEffect(() => {
    beneficiaries.forEach(({ iban, bic }, index) => {
      if (iban == null) return;

      if (isFinnishIBAN(iban) && bic !== null) {
        setValue(`beneficiaries.${index}.bic`, null);
      }

      if (isForeignIBAN(iban) && bic == null) {
        setValue(`beneficiaries.${index}.bic`, '');
      }
    });
  }, [beneficiaries, setValue]);

  // Loads from from session storage in case user is navigating back to page
  useEffect(() => {
    const persistedForm = window.sessionStorage.getItem(FORM_KEY);
    if (persistedForm) {
      const parsed: Partial<NewTerminationNotice> = JSON.parse(persistedForm);
      (Object.entries(parsed) as [keyof NewTerminationNotice, string][]).forEach(([key, value]) => {
        setValue(key, value);
      });
    }
  }, [setValue]);

  // Persists all values to session storage in case user navigates away
  useEffect(() => {
    const subscription = watch((value) => {
      window.sessionStorage.setItem(FORM_KEY, JSON.stringify(value));
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleSubmit = async (data: NewTerminationNotice) => {
    setIsBusy(true);
    const adjustedData = getAdjustedTerminationFormData(data);
    try {
      const strongAuthUrl = await initializeTerminationNotice(adjustedData);
      window.sessionStorage.removeItem(FORM_KEY);
      afterSubmit(strongAuthUrl);
    } catch (e) {
      console.error(e);
    } finally {
      setIsBusy(false);
    }
  };

  const onError = (errors: any) => {
    // Scroll to first error, if exists. Required for InputFields rendered using a custom inputElementRenderer,
    // since these are not attached to react-hook-forms.
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      // Escape the dots in our input ID. These are treated as class identifiers by querySelector.
      const inputId = CSS.escape(`${translationPrefix}.${firstErrorField}`);
      const el = document.querySelector(`#${inputId}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  return (
    <FormPageContainer<NewTerminationNotice> formMethods={formMethods}>
      <H1>{t('title')}</H1>
      {contract.data && (
        <Body2>
          {t('addressDescription')} <b>{formatAddress(contract.data.postalAddress)}. </b>
          {t('addressDescription2')}
        </Body2>
      )}
      <Body2>{t('addressDescription3')}</Body2>
      <Body2>{t('addressDescription4')}</Body2>
      <Body2>{t('addressDescription5')}</Body2>
      <Form onSubmit={formMethods.handleSubmit(handleSubmit, onError)}>
        <TerminationDetailsContent>
          <Description>{t('description')}</Description>
          <FormRow>
            <InputField<NewTerminationNotice, DropdownOptionValue>
              fieldPath="endDate"
              translationPrefix={translationPrefix}
              required
              inputElementRenderer={(props) => <Dropdown {...props} options={contractEndDates} />}
            />
          </FormRow>
          <FormRow>
            <InputField<NewTerminationNotice, DropdownOptionValue>
              fieldPath="terminationReason"
              translationPrefix={translationPrefix}
              required
              inputElementRenderer={(props) => <TerminationReasonDropdown {...props} />}
            />
          </FormRow>
          <FormRow>
            <InputField<NewTerminationNotice, boolean>
              fieldPath="masterKeyAccessAllowed"
              translationPrefix={translationPrefix}
              hideLabel
              inputElementRenderer={({ value, id, onChange }) => (
                <FlexColumn gap="12px">
                  {t('masterKeyAccessAllowed')}
                  <RadioGroup<boolean>
                    id={id}
                    name="masterKeyAccessAllowed"
                    selectedValue={value}
                    onChange={onChange}
                    options={[
                      { label: tCommon('yes'), value: true },
                      { label: tCommon('no'), value: false },
                    ]}
                  />
                </FlexColumn>
              )}
            />
          </FormRow>
          <FormRow>
            <InputField<NewTerminationNotice, boolean>
              fieldPath="hasPetsInApartment"
              translationPrefix={translationPrefix}
              hideLabel
              inputElementRenderer={({ value, id, onChange }) => (
                <FlexColumn gap="12px">
                  {t('hasPetsInApartment')}
                  <RadioGroup<boolean>
                    id={id}
                    name="hasPetsInApartment"
                    selectedValue={value}
                    onChange={onChange}
                    options={[
                      { label: tCommon('yes'), value: true },
                      { label: tCommon('no'), value: false },
                    ]}
                  />
                </FlexColumn>
              )}
            />
          </FormRow>
          <FlexColumn>
            <InputField<NewTerminationNotice, string>
              translationPrefix={translationPrefix}
              fieldPath="additionalDetails"
              inputElementRenderer={(props) => <TextAreaElement {...props} placeholder={t('additionalDetails')} />}
            />
            <Body2>{t('additionalDetailsLabel')}</Body2>
          </FlexColumn>
        </TerminationDetailsContent>

        <DisplayContent>
          <H3>{t('display')}</H3>
          <FormRow>
            <InputField<NewTerminationNotice, boolean>
              fieldPath="givingContactInfoAllowed"
              translationPrefix={translationPrefix}
              hideLabel
              inputElementRenderer={({ value, id, onChange }) => (
                <FlexColumn gap="12px">
                  {t('givingContactInfoAllowed')}
                  <RadioGroup<boolean>
                    id={id}
                    name="givingContactInfoAllowed"
                    selectedValue={value}
                    onChange={onChange}
                    options={[
                      { label: tCommon('yes'), value: true },
                      { label: tCommon('no'), value: false },
                    ]}
                  />
                </FlexColumn>
              )}
            />
          </FormRow>
        </DisplayContent>

        <AddressContent>
          <H3>{t('newAddress.title')}</H3>
          <FormRow>
            <InputField<NewTerminationNotice, string>
              translationPrefix={translationPrefix}
              fieldPath="newAddress.streetAddress"
              type="text"
              required
              placeholder={t('newAddress.streetAddress')}
            />
          </FormRow>
          <FormRow>
            <InputField<NewTerminationNotice, string>
              translationPrefix={translationPrefix}
              fieldPath="newAddress.postalCode"
              type="text"
              required
              placeholder={t('newAddress.postalCode')}
            />
            <InputField<NewTerminationNotice, string>
              translationPrefix={translationPrefix}
              fieldPath="newAddress.city"
              type="text"
              required
              placeholder={t('newAddress.city')}
            />
          </FormRow>
        </AddressContent>

        <BeneficiariesContent>
          <H3>{t('beneficiary')}</H3>
          <Body2>{t('depositInfo')}</Body2>
          {fields.map((field, index) => {
            const iban = beneficiaries?.[index]?.iban;

            return (
              <div key={field.id} style={{ marginBottom: '1rem' }}>
                <InputField<NewTerminationNotice, string>
                  translationPrefix={translationPrefix}
                  fieldPath={`beneficiaries.${index}.beneficiaryName`}
                  type="text"
                  required
                  placeholder={t('beneficiaries.beneficiaryName')}
                />
                <FormRow>
                  <InputField<NewTerminationNotice, string>
                    translationPrefix={translationPrefix}
                    fieldPath={`beneficiaries.${index}.phoneNumber`}
                    type="text"
                    required
                    placeholder={t('beneficiaries.phoneNumber')}
                  />
                </FormRow>
                <FormRow>
                  <InputField<NewTerminationNotice, string>
                    translationPrefix={translationPrefix}
                    fieldPath={`beneficiaries.${index}.email`}
                    type="text"
                    required
                    placeholder={t('beneficiaries.email')}
                  />
                </FormRow>
                <FormRow>
                  <InputField<NewTerminationNotice, string>
                    translationPrefix={translationPrefix}
                    fieldPath={`beneficiaries.${index}.iban`}
                    type="text"
                    required
                    placeholder={t('beneficiaries.iban')}
                  />
                </FormRow>
                {isForeignIBAN(iban) && (
                  <FormRow>
                    <InputField<NewTerminationNotice, string>
                      translationPrefix={translationPrefix}
                      fieldPath={`beneficiaries.${index}.bic`}
                      type="text"
                      required
                      placeholder={t('beneficiaries.bic')}
                    />
                  </FormRow>
                )}
                {fields.length > 1 && (
                  <Button primary onClick={() => remove(index)}>
                    {t('removeBeneficiary')}
                  </Button>
                )}
              </div>
            );
          })}
          <FormRow>
            <Button type="button" icon="plus" onClick={handleAddNewBeneficiary}>
              {t('addNewBeneficiary')}
            </Button>
          </FormRow>
        </BeneficiariesContent>
        <Divider />
        <ButtonContainer>
          <Button primary icon="send2" type="submit" id="send" disabled={isBusy}>
            {t('send')}
          </Button>
          <LinkButton to={YSaatioRoutes.HOME}>{tCommon('back')}</LinkButton>
        </ButtonContainer>
      </Form>
    </FormPageContainer>
  );
};
